import React from 'react';
import CustomIcon from '../Common/CustomIcon/CustomIcon';
import ChonkyFileBrowser from './Chonky/ChonkyFileBrowser';
import {
	ChonkyActions, ChonkyIconName,
} from 'chonky';
import Select from 'react-select';

import API from 'Vulture/lib/API.js';

// Import styles to match Library design
import styles from './Library.module.css';
import modalStyles from './CustomModal.module.css';

// Row kinds used by the Loop Assignments table.
// - 'single':  A system with 0 or 1 VCL brood. Renders one flat row with a
//   VCL Assignment dropdown (disabled when the system has no VCL brood at all).
// - 'parent':  A system with >=2 VCL broods. Renders a header row with the
//   system + property names and NO dropdown.
// - 'child':   One VCL brood under a 'parent' row. Renders an indented row
//   whose System Name is the brood's label, with its own VCL dropdown.
const ROW_KIND_SINGLE = 'single';
const ROW_KIND_PARENT = 'parent';
const ROW_KIND_CHILD  = 'child';


class LoopAssignments extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			assignments:          [],
			selectedAssignments:  [],
			allRowsSelected:      false,
			searchQuery:          '',
			sortColumn:           'systemName',
			sortDirection:        'asc',
			showMultiAssignModal: false,
			confirmationMessage:  '',
			page:                 1,
			itemsPerPage:         50,
			loading:              true,
			fileBrowserCallback:  null,
		};

		this.abortControllerRef = React.createRef();
	}

	componentDidMount() {
		this.loadAssignments();
	}

	componentWillUnmount() {
		// Cleanup abort controller on unmount
		if (this.abortControllerRef.current) {
			this.abortControllerRef.current.abort();
		}
	}

	loadAssignments = async () => {
		const {
			accountUUID,
		} = this.props;

		this.abortControllerRef.current = new AbortController();

		this.setState({
			loading: true,
		});

		try {
			const data = await API.getVCLAssignments(accountUUID, this.abortControllerRef.current.signal);

			if (Array.isArray(data)) {
				this.setState({
					assignments: this.transformAssignments(data),
					loading:     false,
				});
			} else {
				this.setState({
					assignments: [],
					loading:     false,
				});
			}
		} catch (error) {
			if (error.name === 'AbortError') {
				return;
			}
			this.setState({
				assignments: [],
				loading:     false,
			});
		}
	}

	// Convert visual_content_loop_id (numeric, possibly null) to the matching
	// VCL display name, or an empty string when unassigned. The server's
	// component_to_setting_map stores a numeric VCL id, so this only ever
	// matches on numeric id — UUID fallbacks were dead code.
	vclNameForID = (vclID) => {
		if (!vclID) {
			return '';
		}
		const target = Number(vclID);
		const matched = this.props.visualContentLoops.find((loop) => {
			return Number(loop.id) === target;
		});
		return matched ? matched.name : `VCL ${vclID}`;
	}

	// Flatten the per-system response into the table's row model.
	// One single-VCL or zero-VCL system → one 'single' row.
	// A multi-VCL system → one 'parent' row followed by N 'child' rows.
	// Each row carries a stable sortKey so Chonky's name-sort keeps parent +
	// children grouped together regardless of the child's display label.
	transformAssignments = (systems) => {
		const rows = [];

		systems.forEach((system) => {
			const broods = Array.isArray(system.broods) ? system.broods : [];
			const sysName = (system.system_display_name || '').toLowerCase();
			const propName = (system.system_group_display_name || '').toLowerCase();

			if (broods.length <= 1) {
				const brood = broods[0] || null;
				const isVCL = !!(brood && brood.is_vcl);
				rows.push({
					uuid:                `sys-${system.system_id}`,
					rowKind:             ROW_KIND_SINGLE,
					systemName:          system.system_display_name,
					propertyName:        system.system_group_display_name,
					vclAssignment:       isVCL ? this.vclNameForID(brood.visual_content_loop_id) : '',
					isChecked:           false,
					systemId:            system.system_id,
					componentId:         brood ? brood.component_id : null,
					visualContentLoopId: brood ? brood.visual_content_loop_id : null,
					hasBrood:            !!brood,
					isVCL:               isVCL,
					systemSortKey:       `${sysName}|0|0`,
					propertySortKey:     `${propName}|0|0`,
				});
				return;
			}

			// Multi-brood system: parent header row + one child per brood
			// (VCL or not). Non-VCL children render as inert rows downstream
			// so the user sees the system topology without being able to
			// (meaninglessly) assign a VCL to a non-VCL brood.
			const vclChildCount = broods.filter((b) => {
				return b.is_vcl;
			}).length;
			rows.push({
				uuid:           `sys-${system.system_id}`,
				rowKind:        ROW_KIND_PARENT,
				systemName:     system.system_display_name,
				propertyName:   system.system_group_display_name,
				vclAssignment:  '',
				isChecked:      false,
				systemId:       system.system_id,
				componentId:    null,
				vclChildCount,
				// Parent multi-select only fans out to VCL-enabled children —
				// non-VCL broods don't accept an assignment.
				childUuids:     broods.filter((b) => {
					return b.is_vcl;
				}).map((b) => {
					return `sys-${system.system_id}-cmp-${b.component_id}`;
				}),
				// Lets ChonkyFileBrowser's primitive-recursion fallback in
				// `injectedFilteredFiles` match the parent row when a search
				// query hits any of its children's labels.
				childSearchText: broods.map((b) => {
					return b.component_label || '';
				}).join(' '),
				systemSortKey:   `${sysName}|0|0`,
				propertySortKey: `${propName}|0|${system.system_id}-0`,
			});

			broods.forEach((brood, idx) => {
				rows.push({
					uuid:                `sys-${system.system_id}-cmp-${brood.component_id}`,
					rowKind:             ROW_KIND_CHILD,
					systemName:          brood.component_label || 'Unnamed',
					propertyName:        '',
					vclAssignment:       brood.is_vcl ? this.vclNameForID(brood.visual_content_loop_id) : '',
					isChecked:           false,
					systemId:            system.system_id,
					componentId:         brood.component_id,
					visualContentLoopId: brood.visual_content_loop_id,
					isVCL:               !!brood.is_vcl,
					parentUuid:          `sys-${system.system_id}`,
					parentVCLChildCount: vclChildCount,
					// Mirror image of childSearchText on the parent: lets the
					// primitive-recursion search match a child row when the
					// query hits its parent's system or property name.
					parentSearchText:    `${system.system_display_name} ${system.system_group_display_name}`,
					// Sort children right after their parent: same primary key
					// (parent system/property name), bumped secondary segment.
					systemSortKey:       `${sysName}|1|${String(idx).padStart(6, '0')}`,
					propertySortKey:     `${propName}|1|${system.system_id}-${String(idx).padStart(6, '0')}`,
				});
			});
		});

		return rows;
	}

	handleSearch = (e) => {
		this.setState({
			searchQuery:     e.target.value,
			allRowsSelected: false, // Reset selection state when search changes
		});
	}

	clearSearch = () => {
		this.setState({
			searchQuery:     '',
			allRowsSelected: false,
		});
	}

	handleSort = (column) => {
		const {
			sortColumn, sortDirection,
		} = this.state;
		const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc';

		this.setState({
			sortColumn:    column,
			sortDirection: newDirection,
		});
	}

	getFilteredAssignments = () => {
		const {
			assignments, searchQuery,
		} = this.state;

		const query = searchQuery.toLowerCase();
		if (!query) {
			return assignments;
		}

		// Match by system name, property name, or brood label. We track which
		// systems matched and keep the entire group (parent + all children)
		// for any system that has any matching row — so searching for a child
		// brood label reveals its parent header for context, and searching
		// for a system name reveals its full per-VCL breakdown.
		const matchedSystemIds = new Set();
		assignments.forEach((row) => {
			if (row.systemName.toLowerCase().includes(query) ||
				(row.propertyName || '').toLowerCase().includes(query)) {
				matchedSystemIds.add(row.systemId);
			}
		});

		return assignments.filter((row) => {
			return matchedSystemIds.has(row.systemId);
		});
	}

	toggleSelectAllRows = () => {
		const filteredAssignments = this.getFilteredAssignments();
		const {
			assignments, selectedAssignments,
		} = this.state;

		// Check if all filtered rows are currently selected
		const allFilteredSelected = filteredAssignments.every((assignment) => {
			return selectedAssignments.includes(assignment.uuid);
		});

		let newSelectedAssignments = [ ...selectedAssignments, ];

		if (allFilteredSelected) {
			// Unselect all filtered rows
			filteredAssignments.forEach((assignment) => {
				const index = newSelectedAssignments.indexOf(assignment.uuid);
				if (index > -1) {
					newSelectedAssignments.splice(index, 1);
				}
			});
		} else {
			// Select all filtered rows
			filteredAssignments.forEach((assignment) => {
				if (!newSelectedAssignments.includes(assignment.uuid)) {
					newSelectedAssignments.push(assignment.uuid);
				}
			});
		}

		// Update the isChecked property for all assignments
		const newAssignments = assignments.map((assignment) => {
			return {
				...assignment,
				isChecked: newSelectedAssignments.includes(assignment.uuid),
			};
		});

		// Check if all filtered rows are selected after the change
		const newAllFilteredSelected = filteredAssignments.every((assignment) => {
			return newSelectedAssignments.includes(assignment.uuid);
		});

		this.setState({
			assignments:         newAssignments,
			selectedAssignments: newSelectedAssignments,
			allRowsSelected:     newAllFilteredSelected,
		});
	}

	toggleSelectSingleRow = (assignmentUuid) => {
		const {
			assignments, selectedAssignments,
		} = this.state;

		const newAssignments = assignments.map((assignment) => {
			return assignment.uuid === assignmentUuid ? {
				...assignment,
				isChecked: !assignment.isChecked,
			} : assignment;
		}
		);

		const newSelectedAssignments = [ ...selectedAssignments, ];
		const index = newSelectedAssignments.indexOf(assignmentUuid);

		if (index > -1) {
			newSelectedAssignments.splice(index, 1);
		} else {
			newSelectedAssignments.push(assignmentUuid);
		}

		// Check if all filtered rows are selected
		const filteredAssignments = this.getFilteredAssignments();
		const allFilteredSelected = filteredAssignments.every((assignment) => {
			return assignment.uuid === assignmentUuid ? !assignment.isChecked : newSelectedAssignments.includes(assignment.uuid);
		}
		);

		this.setState({
			assignments:         newAssignments,
			selectedAssignments: newSelectedAssignments,
			allRowsSelected:     allFilteredSelected,
		});
	}

	handleSingleAssignment = async (assignmentUuid, vclName) => {
		const {
			accountUUID,
		} = this.props;

		const {
			assignments,
		} = this.state;

		const assignment = assignments.find((a) => {
			return a.uuid === assignmentUuid;
		});
		if (!assignment || !assignment.componentId) {
			this.setState({
				confirmationMessage: 'Error: Assignment not found',
			});
			return;
		}

		// Map the selected VCL name to its numeric id. The backend binds
		// visual_content_loop_id as uint, so a UUID would fail validation.
		let visualContentLoopId = 0;
		if (vclName) {
			const selectedVcl = this.props.visualContentLoops.find((loop) => {
				return loop.name === vclName;
			});
			if (selectedVcl && selectedVcl.id) {
				visualContentLoopId = Number(selectedVcl.id);
			}
		}

		try {
			await API.updateVCLAssignment(
				accountUUID,
				assignment.componentId,
				visualContentLoopId,
				this.abortControllerRef.current?.signal,
			);

			const newAssignments = assignments.map((a) => {
				return a.uuid === assignmentUuid ? {
					...a,
					vclAssignment: vclName,
					visualContentLoopId,
				} : a;
			});

			this.setState({
				assignments:         newAssignments,
				confirmationMessage: 'Assignment Updated Successfully',
			});
		} catch (error) {
			if (error.name !== 'AbortError') {
				this.setState({
					confirmationMessage: 'Error: Failed to update assignment',
				});
			}
		}

		setTimeout(() => {
			this.setState({
				confirmationMessage: '',
			});
		}, 3000);
	}

	openMultiAssignModal = (fileBrowserCallback) => {
		const {
			selectedAssignments,
		} = this.state;

		if (selectedAssignments.length === 0) {
			return;
		}

		this.setState({
			showMultiAssignModal: true,
			fileBrowserCallback,
		});
	}

	closeMultiAssignModal = () => {
		this.setState({
			showMultiAssignModal: false,
			fileBrowserCallback:  null,
		});
	}

	handleMultiAssignment = async (visualContentLoopId, vclName) => {
		const {
			accountUUID,
		} = this.props;

		const {
			assignments,
			selectedAssignments,
			fileBrowserCallback,
		} = this.state;

		// Expand parent rows to their child rows (a parent has no componentId
		// of its own; the assignment lives on each child brood).
		const targetUuids = new Set();
		const selectedSet = new Set(selectedAssignments);
		assignments.forEach((row) => {
			if (!selectedSet.has(row.uuid)) {
				return;
			}
			if (row.rowKind === ROW_KIND_PARENT) {
				// childUuids is already filtered to VCL-enabled broods at
				// transform time; this is the normal fan-out path.
				(row.childUuids || []).forEach((id) => {
					return targetUuids.add(id);
				});
				return;
			}
			// Defense-in-depth: a non-VCL brood should never reach this point
			// (it's marked selectable:false in chonkyFiles), but if a row-click
			// quirk ever lets one in, refuse to issue the no-op write.
			if (row.componentId && row.isVCL !== false) {
				targetUuids.add(row.uuid);
			}
		});

		const assignmentsToUpdate = assignments.filter((a) => {
			return targetUuids.has(a.uuid);
		});

		if (assignmentsToUpdate.length === 0) {
			this.setState({
				confirmationMessage:  'Error: No assignments selected',
				showMultiAssignModal: false,
			});
			setTimeout(() => {
				this.setState({
					confirmationMessage: '',
				});
			}, 2000);
			return;
		}

		// Defensive coerce: callers should already pass numeric ids (vclOptions
		// uses Number(loop.id)), but if anything ever wires a UUID through
		// the modal, fail fast with a 0 instead of letting the backend 400.
		const numericVCLID = Number(visualContentLoopId) || 0;

		const responses = await Promise
			.allSettled(assignmentsToUpdate.map((assignment) => {
				return API.updateVCLAssignment(
					accountUUID,
					assignment.componentId,
					numericVCLID,
					this.abortControllerRef.current?.signal,
				).then(() => {
					return assignment.uuid;
				});
			}));

		const successfulAssignments = responses
			.filter((res) => {
				return res.status === 'fulfilled';
			})
			.map((res) => {
				return res.value;
			});

		const hasError = successfulAssignments.length !== assignmentsToUpdate.length;

		const successSet = new Set(successfulAssignments);
		const newAssignments = assignments.map((a) => {
			if (successSet.has(a.uuid)) {
				return {
					...a,
					vclAssignment:       vclName,
					visualContentLoopId: numericVCLID,
				};
			}
			return a;
		});

		this.setState({
			assignments:          newAssignments,
			selectedAssignments:  [],
			allRowsSelected:      false,
			showMultiAssignModal: false,
			confirmationMessage:  hasError ? 'Error: Some assignments failed to update, please, refresh the page' : 'Assignments Updated',
		}, () => {
			if (fileBrowserCallback) {
				fileBrowserCallback();
			}
		});

		setTimeout(() => {
			this.setState({
				confirmationMessage: '',
				fileBrowserCallback: null,
			});
		}, hasError ? 5000 : 2000);
	}

	getFilteredAndSortedAssignments = () => {
		const {
			assignments, searchQuery, sortColumn, sortDirection,
		} = this.state;

		// Filter by search query across all columns
		let filteredAssignments = assignments.filter((assignment) => {
			return assignment.systemName.toLowerCase().includes(searchQuery.toLowerCase()) ||
			assignment.propertyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
			assignment.vclAssignment.toLowerCase().includes(searchQuery.toLowerCase());
		}
		);

		// Sort
		filteredAssignments.sort((a, b) => {
			let aVal = a[sortColumn];
			let bVal = b[sortColumn];

			if (sortDirection === 'asc') {
				return aVal > bVal ? 1 : -1;
			}
			return aVal < bVal ? 1 : -1;

		});

		return filteredAssignments;
	}

	render() {
		const {
			visualContentLoops,
			onClose,
		} = this.props;

		const {
			selectedAssignments,
			showMultiAssignModal,
			confirmationMessage,
			loading,
		} = this.state;

		const filteredAssignments = this.getFilteredAssignments();

		// Build Chonky file structures for assignments.
		// Selection rules (PM-driven, DEV-11279):
		//   - Single rows whose one brood isn't VCL-enabled are read-only.
		//   - Zero-brood singles remain selectable (prior behavior preserved).
		//   - Once a system breaks into parent/child layout, the parent is
		//     purely a topology header — never selectable. Each VCL child
		//     gets its own checkbox. Non-VCL children are inert.
		const chonkyFiles = filteredAssignments.map((assignment) => {
			let selectable = true;
			if (assignment.rowKind === ROW_KIND_PARENT) {
				selectable = false;
			} else if (assignment.rowKind === ROW_KIND_CHILD) {
				selectable = !!assignment.isVCL;
			} else if (assignment.rowKind === ROW_KIND_SINGLE && assignment.hasBrood && assignment.isVCL === false) {
				selectable = false;
			}

			return {
				id:             assignment.uuid,
				name:           assignment.systemName, // base name column
				isDir:          false,
				selectable,
				assignmentData: assignment,
			};
		});

		const chonkyFileMap = {
			root: {
				id:          'root',
				name:        'Loop Assignments',
				isDir:       true,
				childrenIds: chonkyFiles.map((f) => {
					return f.id;
				}),
				parentId: null,
			},
			...Object.fromEntries(chonkyFiles.map((f) => {
				return [ f.id, f, ];
			})),
		};

		const injectedColumns = [
			{
				id:       'systemName',
				label:    'System Name',
				accessor: (f) => {
					const a = f.assignmentData;
					// The `value` prop is plucked by ChonkyFileBrowser's
					// resolveSortValue so sorting uses the row's stable
					// sortKey instead of the visible label — that keeps each
					// parent and its children adjacent.
					if (a.rowKind === ROW_KIND_CHILD) {
						return (
							<span value={a.systemSortKey} data-test-id="vclAssignmentRowChild" style={{
								display:     'inline-block',
								paddingLeft: '40px',
								color:       '#444',
								borderLeft:  '3px solid #d0d7de',
								marginLeft:  '4px',
							}}>
								<CustomIcon icon='arrow-turn-down-right'/> {a.systemName}
							</span>
						);
					}
					if (a.rowKind === ROW_KIND_PARENT) {
						return (
							<span value={a.systemSortKey} data-test-id="vclAssignmentRowParent" style={{
								fontWeight: 600,
							}}>
								{a.systemName}
							</span>
						);
					}
					return (
						<span value={a.systemSortKey} data-test-id="vclAssignmentRowSingle">
							{a.systemName}
						</span>
					);
				},
			},
			{
				id:       'propertyName',
				label:    'Property Name',
				accessor: (f) => {
					const a = f.assignmentData;
					return (
						<span value={a.propertySortKey}>
							{a.propertyName}
						</span>
					);
				},
			},
			{
				id:       'vclAssignment',
				label:    'VCL Assignment',
				accessor: (f) => {
					const a = f.assignmentData;

					// Parent header row: no dropdown; the assignment lives on
					// each child brood row below.
					if (a.rowKind === ROW_KIND_PARENT) {
						return null;
					}

					// Non-VCL component on the same system (e.g. a PNL alongside
					// a VCL) is shown for topology context but can't take a
					// VCL assignment.
					if (a.hasBrood !== false && a.isVCL === false) {
						return (
							<span data-test-id="vclAssignmentNotVcl" style={{
								color:      '#999',
								fontStyle:  'italic',
								fontSize:   '13px',
							}}>
								Not a VCL+ Loop
							</span>
						);
					}

					// Singles without a brood are read-only "System Not Configured".
					const disabled = a.rowKind === ROW_KIND_SINGLE && !a.hasBrood;

					return (
						<select
							data-test-id="vclAssignmentSelect"
							value={a.vclAssignment}
							disabled={disabled}
							onChange={(e) => {
								return this.handleSingleAssignment(a.uuid, e.target.value);
							}}
							onClick={(e) => {
								e.stopPropagation();
							}}
							onMouseDown={(e) => {
								e.stopPropagation();
							}}
							onFocus={(e) => {
								e.stopPropagation();
							}}
							onKeyDown={(e) => {
								e.stopPropagation();
							}}
							style={{
								width:        '100%',
								padding:      '4px',
								border:       '1px solid #ccc',
								borderRadius: '4px',
								background:   disabled ? '#f5f5f5' : '#fff',
								color:        disabled ? '#999' : '#000',
								cursor:       disabled ? 'not-allowed' : 'pointer',
							}}
						>
							<option value="">
								{disabled ? 'System Not Configured' : 'Choose VCL Name'}
							</option>
							{visualContentLoops.map((loop) => {
								return <option key={loop.uuid} value={loop.name}>{loop.name}</option>;
							})}
						</select>
					);
				},
			},
		];

		const injectedActions = [
			{
				id: 'toggle_select_all',
				name: 'Select All',
				icon: ChonkyIconName.selectAllFiles,
				toolbar: true,
				contextMenu: false,
				handler: ({ files = [], selection = [], setSelection, clearSelection }) => {
					try {
						const visibleIds = (files || []).map((f) => f.id);
						if (visibleIds.length === 0) return;
						const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selection.includes(id));
						if (allSelected) {
							if (typeof clearSelection === 'function') clearSelection();
						} else {
							if (typeof setSelection === 'function') setSelection(visibleIds);
						}
					} catch (e) {
						console.error('Select All action error:', e);
					}
				},
			},
			{
				id:      'multi_assign',
				name:    'Multiple Assignments',
				icon:    ChonkyIconName.share,
				handler: ({
					setSelection,
				}) => {
					if (this.state.selectedAssignments.length) {
						this.openMultiAssignModal(setSelection);
					}
				},
				disabled: this.state.selectedAssignments.length === 0,
			},
		];

		const vclOptions = [
			{
				value: 0,
				label: 'Choose VCL Name',
			},
			// Use numeric id, not uuid: the backend binds
			// visual_content_loop_id as uint and rejects UUID strings.
			...visualContentLoops.map((loop) => {
				return {
					value: Number(loop.id),
					label: loop.name,
				};
			}),
		];

		// Multi-assignment modal
		let multiAssignModalElement = '';
		if (showMultiAssignModal) {
			multiAssignModalElement = (
				<div className={modalStyles.modalWrapper} onClick={this.closeMultiAssignModal}>
					<div className={`${modalStyles.modal} ${modalStyles.small} ${styles.modalOverflow}`} onClick={(e) => {
						return e.stopPropagation();
					}}>
						<div className={modalStyles.modalContainer}>
							<div className={modalStyles.modalHeader}>
								<div>Multiple Assignments</div>
								<div className={modalStyles.modalClose} onClick={this.closeMultiAssignModal}>
									<CustomIcon icon='times'/>
								</div>
							</div>
							<div className={`${modalStyles.modalContent} ${styles.modalContent}`}>
								<div style={{
									marginBottom: '1em',
									width: 	      '100%',
								}}>
									<label style={{
										display:      'block',
										fontWeight:   600,
										marginBottom: '.5em',
									}}>Select VCL Name</label>

									<Select
										hideSelectedOptions={false}
										className={styles.inputOverflow}
										options={vclOptions}
										onChange={(e) => {
											this.handleMultiAssignment(e.value, e.label);
										}}
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
			);
		}

		// Confirmation message
		let confirmationElement = '';
		if (confirmationMessage) {
			confirmationElement = (
				<div style={{
					position:     'fixed',
					top:          '20px',
					right:        '20px',
					background:   '#28a745',
					color:        'white',
					padding:      '10px 20px',
					borderRadius: '4px',
					zIndex:       1000,
				}}>
					{confirmationMessage}
				</div>
			);
		}

		return (
			<div className="systems-page fade-in">
				{multiAssignModalElement}
				{confirmationElement}

				<div className={styles.pageHeader}>
					<h2>Loop Assignments</h2>
				</div>

				{/* Breadcrumb Navigation */}
				<div style={{
					marginBottom: '20px',
				}}>
					<div style={{
						fontSize: '14px',
						color:    '#666',
					}}>
						<span
							style={{
								cursor: 'pointer',
								color:  '#007bff',
							}}
							onClick={onClose}
						>
							VCL Home
						</span>
						<span style={{
							margin: '0 5px',
						}}>/</span>
						<span style={{
							fontWeight: 'bold',
						}}>Loop Assignments</span>
					</div>
				</div>


				{/* Loading State */}
				{loading && (
					<div style={{
						display:        'flex',
						justifyContent: 'center',
						alignItems:     'center',
						padding:        '40px',
						fontSize:       '16px',
						color:          '#666',
					}}>
						<CustomIcon icon="spinner" style={{
							marginRight: '8px',
						}} />
						Loading assignments...
					</div>
				)}

				{/* Chonky Table Replacement */}
				{!loading && (
					<div style={{
						padding: '8px',
						flex: 1,
						display: 'flex',
						flexDirection: 'column',
						minHeight: 0,
					}}>
						<ChonkyFileBrowser
							mode="loopAssignments"
							app={this.props.app}
							readOnly={true}
							accountUUID={this.props.accountUUID}
							injectedFileMap={chonkyFileMap}
							injectedColumns={injectedColumns}
							injectedActions={injectedActions}
							hideActionsColumn={true}
							addSelectAllToggleAction={true}
							defaultFileViewActionId={ChonkyActions.EnableListView.id}
							defaultSortColumn="systemName"
							searchAllColumns={true}
							hideListViewOptions={true}
							hideEditAndDeleteButtons={true}
							onSelectionChange={(selection) => {
								this.setState((prev) => {
									const prevKey = prev.selectedAssignments.slice().sort().join('|');
									const newKey = selection.slice().sort().join('|');
									if (prevKey === newKey) {
										return null;
									}
									return {
										selectedAssignments: selection,
										assignments:         prev.assignments.map((a) => {
											return {
												...a,
												isChecked: selection.includes(a.uuid),
											};
										}),
									};
								});
							}}
						/>
					</div>
				)}

				{/* Results Summary */}
				{!loading && (
					<div data-test-id="vclAssignmentsSummary" style={{
						marginTop: '5px',
						fontSize:  '14px',
						color:     '#666',
					}}>
						Showing {filteredAssignments.length} of {this.state.assignments.length} assignments
						{selectedAssignments.length > 0 && ` (${selectedAssignments.length} selected)`}
					</div>
				)}
			</div>
		);
	}
}

export default LoopAssignments;
